import { FC } from 'react';
import { Toggle } from '@kontentino/ui';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { DemoContentApi } from 'app/modules/demoContent/api/demoContent';
import {
  DemoContentMutationResponse,
  DemoContentState,
} from 'app/modules/demoContent/types';
import { useToast } from 'app/hooks/useToast';
import { ApiClientError } from 'api/client';
import { queryKey } from 'constants/queryKey';
import { useUser } from 'modules/user/userSelector';
import { PageThunk } from 'modules/page/pageActions';

const EXPECTED_COMMENTS_ON_SEED = 13;

const DemoContentToggle: FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const user = useUser();

  const isAdmin = user.role === 'admin';

  const demoContentStateQuery = useQuery(
    queryKey.demoContentState(),
    () => DemoContentApi.getState(),
    {
      enabled: isAdmin,
    },
  );

  const setDemoContentEnabled = useMutation(DemoContentApi.setEnabled, {
    onSuccess: (response: DemoContentMutationResponse) => {
      queryClient.setQueryData(queryKey.demoContentState(), {
        state: response.state,
      });
      dispatch(PageThunk.requestPages());
      queryClient.invalidateQueries();

      if (
        response.action === 'seeded' &&
        response.counts &&
        response.counts.comments < EXPECTED_COMMENTS_ON_SEED
      ) {
        toast(t('demoContent.partialSeedWarning'), 'warning');
      }
    },
    onError: (error: ApiClientError) => {
      toast(error?.userMessage ?? t('somethingWentWrong'), 'error');
    },
  });

  if (!isAdmin) {
    return null;
  }

  const state: DemoContentState | undefined = demoContentStateQuery.data?.state;

  if (!state || state === 'none') {
    return null;
  }

  const checked = state === 'active';

  return (
    <div
      className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-grayscale-20 tw-pt-3"
      data-name="profile-selector-demo-content-toggle"
      title={t('demoContent.toggleDescription')}
    >
      <span className="tw-text-sm tw-text-grayscale-180">
        {t('demoContent.toggleLabel')}
      </span>
      <Toggle
        checked={checked}
        disabled={setDemoContentEnabled.isLoading}
        onChange={() => setDemoContentEnabled.mutate(!checked)}
        size="small"
        data-cy="profile-selector_demo-content-toggle"
      />
    </div>
  );
};

export default DemoContentToggle;
